import {
  API_BASE_URL,
  AUTH_REDIRECT_URI,
  AUTH_SSO_URL,
  AUTH_TOKEN,
  AWS_COGNITO_CLIENT_ID,
  AWS_COGNITO_URL,
  GZIP_COMPRESSION_BYTE_THRESHOLD,
} from '@/config'
import { isNonProd } from '@/environment'
import type { RuleHelperDict } from '@/types'
import {
  AnalyzeApiFactory,
  AuthApiFactory,
  BuildInfoApiFactory,
  CodeApiFactory,
  LabelsApiFactory,
  PoliciesApiFactory,
  PolicyApiFactory,
  RulesApiFactory,
  ScriptKeyApiFactory,
  SearchApiFactory,
  TestApiFactory,
  UserFunctionsApiFactory,
  type AuthorizationCodeResponse,
  type RefreshTokensResponse,
} from '@klarna/cbrms-policy-rules-manager-client-ts'
import axios, { type AxiosPromise } from 'axios'
import pako from 'pako'
import { v4 as uuidv4 } from 'uuid'

const axiosForApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
})

const axiosForCognito = axios.create({
  baseURL: AWS_COGNITO_URL,
  timeout: 15000,
})

const axiosForBuild = axios.create({
  baseURL: '/',
  timeout: 5000,
})

const axiosForHelpers = axios.create({ timeout: 29000 })

const HTTP_METHODS = Object.freeze(['get', 'post', 'put', 'delete'])

axiosForApi.interceptors.response.use(
  response => {
    return response
  },
  error => {
    if (error?.response?.status === 401 && [localStorage, window].every(v => typeof v !== 'undefined')) {
      const lastLocation = window.location.pathname
      localStorage.removeItem('token')
      localStorage.removeItem('refresh_token')
      if (lastLocation !== '/auth') {
        localStorage.setItem('lastKnownLocation', lastLocation)
      }

      if (AUTH_SSO_URL) {
        window.location.href = AUTH_SSO_URL
      }
    }

    return Promise.reject(error)
  }
)

axiosForApi.interceptors.request.use(
  config => {
    // The swagger generated client encodes user's email, we try to decode it here before sending the request
    if (config.url && config.url.includes('/users')) {
      config.url = decodeURIComponent(config.url)
    }

    if (HTTP_METHODS.includes(config.method!)) {
      // Auth token is set in auth.ts module
      const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token')! : AUTH_TOKEN
      if (!token) {
        throw new Error('No auth token available')
      }

      const correlationId = uuidv4()
      // For debugging purposes we need to log the correlation ids of every call on Jenkins (nonprod)
      if (isNonProd) {
        console.log('Klarna-Correlation-Id:', correlationId) // eslint-disable-line
      }

      // Compression is disabled for `localhost` (i.e.: development) because `serverless-offline`
      // does not support decompression
      if (GZIP_COMPRESSION_BYTE_THRESHOLD !== undefined) {
        const isPostOrPut = ['post', 'put'].includes(config.method!)
        const shouldCompress = isPostOrPut && config.data?.length > GZIP_COMPRESSION_BYTE_THRESHOLD

        if (shouldCompress) {
          // We compress POST and PUT payloads it they exceed
          //  `GZIP_COMPRESSION_BYTE_THRESHOLD` bytes in size
          config.data = pako.gzip(config.data)
        }
      }

      const headers = config.headers ?? {}
      headers['Content-Type'] = 'application/json'
      headers['Klarna-Correlation-Id'] = correlationId
      headers['Authorization'] = `Bearer ${token}`
      config.headers = headers
    }

    return config
  },
  error => Promise.reject(error)
)

const codeApi = CodeApiFactory(undefined, API_BASE_URL, axiosForApi)
const scriptKeys = ScriptKeyApiFactory(undefined, API_BASE_URL, axiosForApi)
const scriptKeysApi = {
  ...scriptKeys,
  getHelpers: async (): Promise<RuleHelperDict> => {
    // This call gets a temporary url to retrieve helpers file from the BE
    const { data: temporaryUrl } = await scriptKeys.getHelpersUrl()

    const helpers = axiosForHelpers.get((temporaryUrl as any).message).then(res => res.data)

    return helpers
  },
}
const testApi = TestApiFactory(undefined, API_BASE_URL, axiosForApi)
const rulesApi = RulesApiFactory(undefined, API_BASE_URL, axiosForApi)
const policyApi = PolicyApiFactory(undefined, API_BASE_URL, axiosForApi)
const policiesApi = PoliciesApiFactory(undefined, API_BASE_URL, axiosForApi)
const policyAnalysis = AnalyzeApiFactory(undefined, API_BASE_URL, axiosForApi)
const userFunctionsApi = UserFunctionsApiFactory(undefined, API_BASE_URL, axiosForApi)
const searchApi = SearchApiFactory(undefined, API_BASE_URL, axiosForApi)
const buildInfoApi = BuildInfoApiFactory(undefined, '', axiosForBuild)
const labelsApi = LabelsApiFactory(undefined, API_BASE_URL, axiosForApi)
const { getOrRefreshTokens } = AuthApiFactory(undefined, AWS_COGNITO_URL, axiosForCognito)
const { userLogin, getUserInfo } = AuthApiFactory(undefined, API_BASE_URL, axiosForApi)
const authApi = {
  userLogin,
  getUserInfo,
  getTokens: (authorizationCode: string) => {
    return getOrRefreshTokens(
      'application/x-www-form-urlencoded',
      'authorization_code',
      authorizationCode,
      AWS_COGNITO_CLIENT_ID,
      AUTH_REDIRECT_URI
    ) as AxiosPromise<AuthorizationCodeResponse>
  },
  refreshTokens: (refreshToken: string) => {
    return getOrRefreshTokens(
      'application/x-www-form-urlencoded',
      'refresh_token',
      undefined,
      AWS_COGNITO_CLIENT_ID,
      undefined,
      refreshToken
    ) as AxiosPromise<RefreshTokensResponse>
  },
}

const apiService = {
  ...scriptKeysApi,
  ...testApi,
  ...codeApi,
  ...rulesApi,
  ...policiesApi,
  ...policyAnalysis,
  ...userFunctionsApi,
  ...policyApi,
  ...searchApi,
  ...buildInfoApi,
  ...labelsApi,
  ...authApi,
}

export default apiService
